interface BundleGiftPreviewProps {
  settings: any;
}

export const BundleGiftPreview = ({ settings }: BundleGiftPreviewProps) => {
  const gifts = settings.gifts || [];
  const style = settings.style || "cards";
  
  // Calculate total value
  const totalValue = gifts.reduce((sum: number, gift: any) => {
    const value = parseInt(gift.value?.replace(/[^0-9]/g, '') || '0');
    return sum + value;
  }, 0);

  const renderIcon = (icon: string) => {
    if (icon.startsWith('fa-')) {
      return <i className={`fas ${icon} text-3xl`}></i>;
    }
    return <span className="text-4xl">{icon}</span>;
  };

  const containerStyle = {
    backgroundColor: settings.backgroundColor || '#fef3c7',
    borderRadius: `${settings.borderRadius || 16}px`,
    padding: `${settings.padding || 32}px`,
  };

  const titleStyle = {
    color: settings.titleColor || '#000000',
  };

  const textStyle = {
    color: settings.textColor || '#374151',
  };

  const cardStyle = {
    backgroundColor: settings.cardColor || '#ffffff',
    borderRadius: `${(parseInt(settings.borderRadius) || 16) / 2}px`,
  };

  if (gifts.length === 0) {
    return (
      <div className="w-full py-8 px-4" style={containerStyle}>
        <p className="text-center text-muted-foreground">لم يتم إضافة هدايا بعد</p>
      </div>
    );
  }

  return (
    <div className="w-full py-8 px-4" style={containerStyle}>
      <div className="max-w-6xl mx-auto">
        <h2 
          className="text-3xl font-bold text-center mb-4"
          style={titleStyle}
        >
          {settings.title || "احصل على هذه الهدايا مجاناً!"}
        </h2>
        
        {settings.description && (
          <p 
            className="text-center mb-8 text-lg"
            style={textStyle}
          >
            {settings.description}
          </p>
        )}

        {style === "cards" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gifts.map((gift: any, index: number) => (
              <div
                key={index}
                className="p-6 shadow-lg transition-transform hover:scale-105"
                style={{
                  ...cardStyle,
                  animation: settings.showAnimation ? `fadeInUp 0.5s ease-out ${index * 0.1}s both` : 'none',
                }}
              >
                <div className="text-center mb-4">
                  {renderIcon(gift.icon)}
                </div>
                <h3 className="text-xl font-bold text-center mb-2" style={titleStyle}>
                  {gift.title}
                </h3>
                <p className="text-center mb-3" style={textStyle}>
                  {gift.description}
                </p>
                {gift.value && (
                  <p className="text-center font-bold text-green-600">
                    قيمة: {gift.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {style === "list" && (
          <div className="space-y-4">
            {gifts.map((gift: any, index: number) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 shadow"
                style={{
                  ...cardStyle,
                  animation: settings.showAnimation ? `fadeInRight 0.5s ease-out ${index * 0.1}s both` : 'none',
                }}
              >
                <div className="flex-shrink-0">
                  {renderIcon(gift.icon)}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1" style={titleStyle}>
                    {gift.title}
                  </h3>
                  <p style={textStyle}>{gift.description}</p>
                </div>
                {gift.value && (
                  <div className="flex-shrink-0 text-right">
                    <p className="font-bold text-green-600">{gift.value}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {style === "grid" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gifts.map((gift: any, index: number) => (
              <div
                key={index}
                className="p-4 text-center shadow"
                style={{
                  ...cardStyle,
                  animation: settings.showAnimation ? `zoomIn 0.5s ease-out ${index * 0.1}s both` : 'none',
                }}
              >
                <div className="mb-2">
                  {renderIcon(gift.icon)}
                </div>
                <h3 className="text-sm font-bold mb-1" style={titleStyle}>
                  {gift.title}
                </h3>
                {gift.value && (
                  <p className="text-xs font-bold text-green-600">{gift.value}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {style === "highlight" && (
          <div className="space-y-6">
            {gifts.map((gift: any, index: number) => (
              <div
                key={index}
                className="relative p-8 shadow-xl overflow-hidden"
                style={{
                  ...cardStyle,
                  animation: settings.showAnimation ? `fadeInUp 0.5s ease-out ${index * 0.1}s both` : 'none',
                }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400 opacity-10 rounded-full -mr-16 -mt-16"></div>
                <div className="relative z-10 flex items-center gap-6">
                  <div className="flex-shrink-0 bg-yellow-100 p-4 rounded-full">
                    {renderIcon(gift.icon)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2" style={titleStyle}>
                      {gift.title}
                    </h3>
                    <p className="text-lg" style={textStyle}>{gift.description}</p>
                  </div>
                  {gift.value && (
                    <div className="flex-shrink-0 text-right">
                      <p className="text-2xl font-bold text-green-600">{gift.value}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {settings.showTotalValue && totalValue > 0 && (
          <div className="mt-8 text-center p-6 bg-green-100 rounded-lg">
            <p className="text-2xl font-bold text-green-800">
              القيمة الإجمالية للهدايا: {totalValue} جنيه
            </p>
            <p className="text-lg text-green-700 mt-2">
              كل هذا مجاناً عند شرائك للمنتج الآن! 🎉
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};
